import { useParams } from "react-router-dom";
import MTable from "../../components/mtable";
import Input, { clearForm, FormState, required, submitForm, useAreaInput, useInput, useTextInput, useToggleInput } from "../../components/input";
import { api } from "../../env";
import { memo, useEffect, useState } from "react";
import { date } from "../../utils/date";
import { ConfirmButton } from "../../components/ConfirmButton";
import { MeetingType } from "../../utils/enums";
import useIsMobile from "../../hooks/isMobile";

const Meetings = memo(function Meetings({chapterId, enrollmentId, onActiveChange}) {

    const { id } = useParams()
    const isTablet = useIsMobile(1350)

    const getNote = (meeting) => {
        if (meeting.content == '') return 'Notes not provided yet'
        return meeting.content
    }

    const getLocation = (meeting) => {
        switch(meeting.type) {
            case MeetingType.IN_PERSON: return "In Person"
            case MeetingType.VIRTUAL: return "Virtual"
            default: return "Other Meeting Method"
        }
    }

    const getIsPrivate = (meeting) => {
        if (meeting.isPrivate) return "Private"
        return "Shared"
    }

    const mapper = {columns: [
        {
            label: "Date",
            value: (item) => date(item.meetingDate)
        },
        {
            label: "Visibility",
            value: (item) => getIsPrivate(item)
        },
        {
            label: "Note",
            value: (item) => getNote(item)
        }
    ]}

    const [state, setState] = useState(FormState.UNKNOWN)
    const [selected, setSelected] = useState(undefined)

    useEffect(() => { onActiveChange?.(state !== FormState.UNKNOWN) }, [state])

    const onView = (meeting) => {
        setSelected(meeting)
        setState(meeting ? FormState.VIEW : FormState.UNKNOWN)
        clearForm(meetingForm)
    }

    const onCancel = () => {
        if (!selected) setState(FormState.UNKNOWN)
        else setState(FormState.VIEW)
        clearForm(meetingForm)
    }

    const onEdit = () => {
        meetingForm.type._setValue(selected?.type)
        meetingForm.meetingDate._setValue(selected?.meetingDate.split("T")[0])
        meetingForm.content._setValue(selected?.content ?? '')
        meetingForm.isPrivate._setValue(selected?.isPrivate)
        setState(FormState.UPDATE)
    }

    const meetingForm = {
        type: useToggleInput("Type", "share_location", true, [{key: MeetingType.IN_PERSON, label: 'In Person'}, {key: MeetingType.VIRTUAL, label: 'Virtual'}]),
        isPrivate: useToggleInput("Visibility", "visibility", false, [{key: true, label: 'Private'}, {key: false, label: 'Shared'}], true),
        meetingDate: useInput("Meeting Date", "event", "date", [required]),
        content: useAreaInput("Notes & Actions [Optional]", "note_stack", [], 7, 15) 
    }

    const saveMeeting = () => {
        if (selected) updateMeeting()
        else createMeeting()
    }

    const createMeeting = () => {
        const meetingNote = submitForm(meetingForm)
        if (!meetingNote) return
        api.post('meeting-notes', {...meetingNote, 'enrollmentId': id, chapterId})
            .then(_ => onCancel())
            .catch(_ => { })
    }

    const updateMeeting = () => {
        const meetingNote = submitForm(meetingForm)
        if (!meetingNote) return
        api.put(`meeting-notes/${selected.id}`, meetingNote)
            .then(_ => onView(undefined))
            .catch(_ => { })
    }

    const deleteMeeting = () => {
        api.delete(`meeting-notes/${selected.id}`)
            .then(_ => onView(undefined))
            .catch(_ => { })
    }

    return(<>
        <div className="flex center justify-center gap-m vi-spacer-s" style={{marginBottom: isTablet ? 'var(--xs)' : '42px'}}>
            <p className="section-title-s accent bold">Meetings</p>
            <button className={`solid-accent ${state == FormState.UNKNOWN ? '' : 'disabled'}`} disabled={state != FormState.UNKNOWN} onClick={() => setState(FormState.CREATE)}>Add</button>
        </div>
        
        {(state == FormState.CREATE || state == FormState.UPDATE) && <div className="card">
            <div className="showing flex columns gap-xs">
                <p className="card-title v-spacer-xs">{state == FormState.CREATE ? 'Create' : 'Update'} Meeting</p>
                <Input state={meetingForm.type}/>
                <Input state={meetingForm.isPrivate}/>
                <Input state={meetingForm.meetingDate}/>
                <Input state={meetingForm.content}/>
                <div className="flex justify-end gap-xs vi-spacer-xs">
                    <button className="outline" onClick={onCancel}>Cancel</button>
                    <button className="solid" onClick={saveMeeting}>{state == FormState.CREATE ? 'Create' : 'Save'}</button>
                </div>
            </div>
        </div>}

        {state == FormState.VIEW && <div className="card">
            <div className="showing">
                <p className="card-label neutral v-spacer-xs">{date(selected.meetingDate)} • {getLocation(selected)} • {getIsPrivate(selected)}</p>
                <p className="card-body standard-padding-xs v-spacer-xs pre-wrap">{getNote(selected)}</p>
                <div className="flex center justify-end gap-xs">
                    <button className="outline" onClick={() => onView(undefined)}>Close</button>
                    {enrollmentId == selected.enrollmentId && <>
                        <ConfirmButton label={'Delete'} className={'outline'} onClick={deleteMeeting}/>
                        <button className="solid" onClick={onEdit}>Edit</button>
                    </>}
                </div>
            </div>
        </div>}

        {state == FormState.UNKNOWN && <MTable path={`enrollments/${id}/meeting-notes`} queryParams={{chapterId}} mapper={mapper} cardView={true} onClick={onView} autoHideNavigation={true} itemsPerPage={20}/>}
    </>)
})

export default Meetings